import { AutocompleteInput } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput';
import type {
  SearchEvent,
  SearchResult,
} from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput.types';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import classNames from 'classnames';
import { type ReactElement, useState } from 'react';
import { useMemo } from 'react';
import type { GroupSearchItem } from './utils/extractGroupSearchItems';
import { groupItemsByTab } from './utils/groupItemsByTab';

export type MenuItem = {
  label: string;
  onClick: () => void;
  index: number;
  tabName: string;
};

interface Props {
  menuItemElements?: ReactElement[];
  groupSearchItems?: GroupSearchItem[];
  onGroupSelect?: (
    tabName: string,
    sectionId?: string,
    subTabName?: string,
  ) => void;
}

export const DiscoverySearch = (props: Props) => {
  const [value, setValue] = useState('');
  const [filteredItems, setFilteredItems] = useState<
    (MenuItem | GroupSearchItem)[]
  >([]);

  // Extract menu item labels from the MenuItemsColumn element
  const menuItems = useMemo(() => {
    const items: MenuItem[] = [];

    const extractLabels = (children: ReactElement[]): void => {
      for (const [index, child] of children.entries()) {
        const anyChild = child as any;
        if (
          anyChild &&
          typeof anyChild === 'object' &&
          'props' in anyChild &&
          anyChild.props.label &&
          anyChild.props.onClick
        ) {
          items.push({
            label: anyChild.props.label,
            onClick: anyChild.props.onClick,
            index,
            tabName: anyChild.key,
          });
        }
      }
    };

    if (props.menuItemElements) {
      extractLabels(props.menuItemElements);
    }

    return items;
  }, [props.menuItemElements]);

  // Combine menu items with group search items
  const allSearchableItems = useMemo(() => {
    const items: (MenuItem | GroupSearchItem)[] = [...menuItems];

    if (props.groupSearchItems) {
      const menuItemLabels = new Set(menuItems.map((item) => item.label));
      for (const item of props.groupSearchItems) {
        if (!menuItemLabels.has(item.label)) {
          items.push(item);
        }
      }
    }

    return items;
  }, [menuItems, props.groupSearchItems]);

  const handleSearch = async (
    searchEvent: SearchEvent,
  ): Promise<SearchResult<MenuItem | GroupSearchItem>> => {
    const { inputValue } = searchEvent;

    const escapedInput = inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escapedInput}`, 'i');

    const filtered = inputValue.trim()
      ? allSearchableItems.filter((item) => regex.test(item.label))
      : [];

    const groupedItems = groupItemsByTab(filtered, menuItems);
    setFilteredItems(groupedItems);

    return {
      currentPage: searchEvent.nextPageNumber,
      paginationLimit: groupedItems.length,
      totalPageCount: 1,
      totalRowsCount: groupedItems.length,
      content: [],
    };
  };

  const handleOptionSelect = (item: MenuItem | GroupSearchItem) => {
    if ('onClick' in item) {
      props.onGroupSelect?.(item.tabName);
    } else if ('tabName' in item && props.onGroupSelect) {
      props.onGroupSelect(item.tabName, item.sectionId, item.subTabName);
    }
    setValue('');
  };

  const renderOption = (option: MenuItem | GroupSearchItem) => {
    const isMenuItem = 'onClick' in option;

    // Highlight the matching part of the label
    const renderLabelWithHighlight = () => {
      if (!value.trim()) {
        return option.label;
      }

      const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedValue})`, 'i');
      const parts = option.label.split(regex);

      return parts.map((part, index) =>
        regex.test(part) ? (
          <strong key={`${index}-${part}`}>{part}</strong>
        ) : (
          <span key={`${index}-${part}`}>{part}</span>
        ),
      );
    };

    return (
      <div className='flex items-center gap-x-2 w-full'>
        <Icon
          glyph='fa-list-tree'
          className={classNames(!isMenuItem && 'invisible')}
        />
        <div>
          <div className='flex-1'>{renderLabelWithHighlight()}</div>
          <div className='text-sm opacity-40'>
            {!isMenuItem && option.path?.join(' / ')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AutocompleteInput.Record
      value={value}
      onChange={setValue}
      onClear={() => setValue('')}
      options={filteredItems}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => {
        if ('onClick' in option) {
          return `${option.label}-${option.tabName}`;
        }
        return `${option.label}-${option.tabName}-${option.sectionId ?? ''}-${(option.path ?? []).join('/')}`;
      }}
      isOptionEqualToValue={(option, val) => option.label === val}
      onOptionSelect={handleOptionSelect}
      onSearch={handleSearch}
      startAdornment={<Icon glyph='fa-search' />}
      placeholder='Search settings'
      renderOption={renderOption}
    />
  );
};
